import { NamespaceLoggerAdapter, getEnvironmentConfigFromRing } from '@fluidx/utilities';
import { DriverInfo } from './DriverInfo';
import { loadComponentInIframeInternal } from './Iframe';
import { HostConfig, OfficeWebHostScope } from './OfficeWebHostTypes';
import { LoadInIframeResult } from './Iframe-shared/LoadInIframeResult';
// Task 4883749: Make `sourcelessIframeBundleUrl` required in future version of OWH
// When resolving that task, uncomment the following line:
// import * as localStorageKeys from './Iframe-sourceless/localStorageKeys';
import { npmVersionNumber } from './npm.package.version';

/** Additional optional configuration when calling `loadComponentInIframe` */
export interface IframeLoadOptions {
  /**
   * The iframe service environment. If none specified, will default to Prod.
   * Note that if setting this parameter, and if you call "clearPersistedStorageInIframe" elsewhere
   * in your code, you will want to pass in the same iframeEnvironment to that function.
   */
  iframeEnvironment?: 'Prod' | 'Dogfood';

  /**
   * The timeout (in milliseconds) for waiting for the inside of the iframe to load,
   * before declaring the load to have failed.
   * The default is 10000 ms (i.e., 10 seconds).
   * If the host wants to wait indefinitely and/or handle this externally
   * rather than relying on a timeout, set this property to 0 or a negative number.
   *
   * Note that for ad-hoc testing, the timeout can also be overwritten using LocalStorage flags: see
   * <https://office.visualstudio.com/OC/_wiki/wikis/Fluid%20Framework%20Experiences/15005/OfficeWebHost>
   * for more info.
   */
  iframeLoadTimeout?: number;

  /**
   * A flag to indicate whether the service worker should be skipped.
   * Defaults to "false" (i.e., service worker NOT disabled)
   */
  skipServiceWorker?: boolean;

  /**
   * [For internal or testing use only]
   * A URL override in place of the environment specified by "iframeEnvironment".
   */
  DoNotUse_iframeEnvironmentOverrideUrl?: string; // eslint-disable-line @typescript-eslint/naming-convention

  /**
   * Title for iframe element to be read by screen readers.
   */
  iframeTitle: string;
}

export const DefaultIframeLoadOptions = {
  iframeLoadTimeout: 10000 /* keep this number in sync with the IframeLoadOptions description above! */
};

/**
 * Loads a component within an isolated iframe, into a provided hosting div,
 * given appropriate information and auth callbacks.
 * Failure to load will result in promise rejection with information
 * about the error.
 * @param driverInfo Driver specific information needed for performing Fluid load operations
 * @param componentDiv A div or a promise to a div that the component will be rendered into
 * @param scope An IFluidObject object that will be available to all containers/components loaded
 * @param config Additional information allowing a host to have richer integration with how components are initialized
 * @param iframeUrl DEPRECATED: An iframe override URL.
 *   Please use "options.iframeEnvironment" instead.
 *   This parameter will be removed in the next major-version bump of Office Web Host.
 * @param options Additional options
 */
export function loadComponentInIframe(
  driverInfo: DriverInfo,
  componentDiv: HTMLElement,
  scope: OfficeWebHostScope,
  config: HostConfig,
  options: IframeLoadOptions
): LoadInIframeResult {
  // The moment we should consider the start of office-web-host execution, which is used for end to end performance analysis.
  const bootstrapperStartTime = Date.now();

  const { codeLoader, logger: hostLogger, environment, ...partialConfig } = config;

  const logger =
    hostLogger &&
    new NamespaceLoggerAdapter('OfficeWebHost', hostLogger, {
      inIframe: true,
      owhVersion: npmVersionNumber
    });

  if (codeLoader) {
    console.warn('Iframes do not support "HostConfig.codeLoader". The code loader will be ignored.');
  }

  // If `HostConfig.environment` is missing, fall back to the iframe environment as a reasonable default.
  const newConfig = {
    ...partialConfig,
    environment: environment || getEnvironmentConfigFromRing(options.iframeEnvironment)
  };

  return loadComponentInIframeInternal(
    { type: 'Load', driverInfo },
    scope.TokenProvider,
    scope,
    componentDiv,
    newConfig,
    logger,
    bootstrapperStartTime,
    options
  );
}

// cspell:ignore bootstrapper srcdoc iframes
